import { MuiColorInput, MuiColorInputColors } from "mui-color-input";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Slider,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { TabPanel } from "../../../../../components/TabPanel";
import { uuidv4 } from "../../../../../utils";
import { OrientationType } from "../../../../../utils/spots";
import { useLocalRampState } from "../RampEditorPresenter";
import { LineMetadata, Shape, SpotMetadata, ZoneMetadata } from "./RampCanvas";
import { SpotPropertiesSection } from "./SpotPropertiesSection";
import { FILL_PATTERNS } from "./patterns/usePatterns";
import { detectRequiredSpots } from "./utils/gapSpotDetection";

export const TAG_OPTIONS = [
  "hangar",
  "parkable",
  "obstruction",
  "utilization",
  "reference_line",
  "spot",
  "zone",
  "egress_lane",
];

export const PREFIXED_COLORS = [
  {
    label: "Parking Area",
    stroke_style: "#3c6ce9",
    fill_style: "rgba(238, 239, 243, 0.10)",
  },
  {
    label: "Hazard Area",
    stroke_style: "#FF0000",
    fill_style: "#FF000030",
  },
  {
    label: "Taxi / Runway Area",
    stroke_style: "#eed202",
    fill_style: "#eed20230",
  },
  {
    label: "Free Space Area",
    stroke_style: "#00FF00",
    fill_style: "rgba(0, 255, 0, 0.38)",
  },
  {
    label: "Ground Vehicles Area",
    stroke_style: "#FCFAFF",
    fill_style: "#FCFAFF30",
  },
  {
    label: "Hangar Area",
    fill_style: "#c8cfde50",
    stroke_style: "#3e5d8c",
  },
];

export const SHAPE_COLORS = [
  "rgba(255, 0, 0, 0.5)",
  "rgba(255, 255, 0, 0.5)",
  "rgba(0, 255, 0, 0.5)",
  "rgba(0, 0, 255, 0.5)",
];

export const TEXT_COLORS = [
  "black",
  "white",
  "red",
  "green",
  "blue",
  "yellow",
  "purple",
  "orange",
  "pink",
  "brown",
];

export const LINE_STYLES = ["solid", "dashed", "dotted"];

export const TEXT_POSITION_OPTIONS = [
  { value: "start", label: "Start" },
  { value: "middle", label: "Middle" },
  { value: "end", label: "End" },
] as const;

type Props = {
  contextMenu: {
    x: number;
    y: number;
    shapeId: string;
  } | null;
  setContextMenu: (contextMenu: Props["contextMenu"]) => void;
  onClose: () => void;
  shapes: Shape[];
  setShapes: (shapes: Shape[]) => void;
  canvasHeight?: number;
  canvasWidth?: number;
  previewSpots?: Shape[];
  setPreviewSpots?: (spots: Shape[]) => void;
};

export const ShapeEditDialog: React.FC<Props> = ({
  contextMenu,
  setContextMenu,
  onClose,
  shapes,
  setShapes,
  canvasHeight: _canvasHeight = 0,
  canvasWidth = 0,
  previewSpots = [],
  setPreviewSpots = () => {},
}) => {
  const { t } = useTranslation();
  const { activeShapes, setActiveShapes, ramp } = useLocalRampState();
  const [activeTab, setActiveTab] = React.useState(0);

  const shape = React.useMemo(
    () => shapes.find((s) => s.id === contextMenu?.shapeId),
    [shapes, contextMenu?.shapeId]
  );

  // Calculate the total number of tabs and reset if out of bounds
  React.useEffect(() => {
    // Early exit if no shape
    if (!shape) return;

    const showAlgorithmTab =
      shape?.tags?.includes("parkable") ||
      shape?.tags?.includes("reference_line") ||
      shape?.tags?.includes("spot") ||
      shape?.type === "Spot" ||
      shape?.type === "Zone";

    const totalTabs = showAlgorithmTab ? 3 : 2;

    if (activeTab >= totalTabs) {
      setActiveTab(totalTabs - 1);
    }
  }, [shape, activeTab]);

  // Keyboard shortcuts for tab switching
  React.useEffect(() => {
    if (!contextMenu || !shape) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+1/2/3 (or Cmd on Mac)
      if (e.ctrlKey || e.metaKey) {
        const showAlgorithmTab =
          shape?.tags?.includes("parkable") ||
          shape?.tags?.includes("reference_line") ||
          shape?.tags?.includes("spot") ||
          shape?.type === "Spot" ||
          shape?.type === "Zone";

        const totalTabs = showAlgorithmTab ? 3 : 2;

        if (e.key === "1") {
          e.preventDefault();
          setActiveTab(0);
        } else if (e.key === "2") {
          e.preventDefault();
          setActiveTab(1);
        } else if (e.key === "3" && totalTabs >= 3) {
          e.preventDefault();
          setActiveTab(2);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu, shape]);

  const setSelectedShapes = (attribute: string, value: any) => {
    const updatedShapes = shapes.map((shape) => {
      if (
        activeShapes.includes(shape.id) ||
        shape.id === contextMenu?.shapeId
      ) {
        return { ...shape, [attribute]: value };
      }
      return shape;
    });
    setShapes(updatedShapes);
  };

  const setSelectedShapesMultiple = (attributes: Record<string, any>) => {
    const updatedShapes = shapes.map((shape) => {
      if (
        activeShapes.includes(shape.id) ||
        shape.id === contextMenu?.shapeId
      ) {
        return { ...shape, ...attributes };
      }
      return shape;
    });
    setShapes(updatedShapes);
  };

  if (!shape) {
    return null;
  }

  const prefixedValue = PREFIXED_COLORS.find(
    (option) =>
      option.stroke_style === shape.stroke_style &&
      option.fill_style === shape.fill_style
  )?.label;

  // Determine if Algorithm tab should be shown
  const showAlgorithmTab =
    shape?.tags?.includes("parkable") ||
    shape?.tags?.includes("reference_line") ||
    shape?.tags?.includes("spot") ||
    shape?.type === "Spot" ||
    shape?.type === "Zone";

  return (
    <Drawer
      open={!!contextMenu}
      onClose={onClose}
      anchor="left"
      hideBackdrop={false}
      ModalProps={{
        BackdropProps: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.1)", // Very light - 10% opacity
          },
        },
      }}
    >
      <Box
        sx={{
          width: 400,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header with title and tabs - fixed */}
        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
            {t('settings.rampsSettings.rampEditor.shapeEdit.title')}
          </Typography>

          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label={t('settings.rampsSettings.rampEditor.shapeEdit.tabs.properties')} />
            <Tab label={t('settings.rampsSettings.rampEditor.shapeEdit.tabs.styling')} />
            {showAlgorithmTab && <Tab label={t('settings.rampsSettings.rampEditor.shapeEdit.tabs.algorithm')} />}
          </Tabs>
        </Box>

        {/* Scrollable tab content area */}
        <Box sx={{ flexGrow: 1, overflowY: "auto", overflowX: "hidden" }}>
          {/* Tab 0: Properties */}
          <TabPanel value={activeTab} index={0}>
            <Stack direction="column" spacing={2}>
              <FormControlLabel
                control={<Switch checked={shape?.locked ?? false} />}
                onChange={() => setSelectedShapes("locked", !shape?.locked)}
                label={t('settings.rampsSettings.rampEditor.shapeEdit.properties.locked')}
              />
              <TextField
                size="small"
                fullWidth
                label={t('settings.rampsSettings.rampEditor.shapeEdit.properties.tags')}
                select
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedShapes(
                    "tags",
                    typeof value === "string" ? value.split(",") : value
                  );
                }}
                value={shape?.tags ?? []}
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    if (Array.isArray(selected)) {
                      return selected.join(", ");
                    }
                    return String(selected);
                  },
                  MenuProps: {
                    PaperProps: {
                      sx: { zIndex: 9999 },
                    },
                  },
                }}
              >
                {TAG_OPTIONS.map((tag) => (
                  <MenuItem key={tag} value={tag}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={shape?.tags?.includes(tag) || false}
                          onChange={(e) => {
                            const currentTags = [...(shape?.tags || [])];
                            if (e.target.checked) {
                              if (!currentTags.includes(tag)) {
                                setSelectedShapes("tags", [
                                  ...currentTags,
                                  tag,
                                ]);
                              }
                            } else {
                              setSelectedShapes(
                                "tags",
                                currentTags.filter((t) => t !== tag)
                              );
                            }
                          }}
                        />
                      }
                      label={tag}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t('settings.rampsSettings.rampEditor.shapeEdit.properties.text')}
                fullWidth
                onChange={(event) => {
                  setSelectedShapes("text", event.target.value);
                }}
                value={shape?.text || ""}
              />
            </Stack>
          </TabPanel>

          {/* Tab 1: Styling */}
          <TabPanel value={activeTab} index={1}>
            <Stack direction="column" spacing={2}>
              <TextField
                size="small"
                fullWidth
                label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.prefixedColors')}
                select
                onChange={(event) => {
                  const option = PREFIXED_COLORS.find(
                    (option) => option.label === event.target.value
                  );
                  setSelectedShapesMultiple({
                    stroke_style: option?.stroke_style,
                    fill_style: option?.fill_style,
                    fill_pattern: "",
                  });
                }}
                sx={{ zIndex: 9999 }}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      sx: { zIndex: 9999 }, // Ensure Menu aligns with Drawer
                    },
                  },
                }}
                value={prefixedValue ?? "custom"}
              >
                {PREFIXED_COLORS.map((option) => (
                  <MenuItem key={option.label} value={option.label}>
                    {option.label}
                  </MenuItem>
                ))}
                <MenuItem value="custom">{t('settings.rampsSettings.rampEditor.shapeEdit.styling.custom')}</MenuItem>
              </TextField>
              {/* select color */}
              <Typography sx={{ mt: 1 }}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.fill')}</Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <MuiColorInput
                  fullWidth
                  size="small"
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.color')}
                  isAlphaHidden={false}
                  onChange={(value: string, colors: MuiColorInputColors) => {
                    setSelectedShapesMultiple({
                      fill_style: colors.hex8,
                      fill_pattern: "",
                    });
                  }}
                  value={shape?.fill_style ?? ""}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.pattern')}
                  select
                  onChange={(event) => {
                    setSelectedShapesMultiple({
                      fill_style: "",
                      fill_pattern: event.target.value,
                    });
                  }}
                  value={shape?.fill_pattern || ""}
                >
                  {FILL_PATTERNS().map((option) => (
                    <MenuItem key={option.label} value={option.label}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <img
                          src={option.value}
                          style={{
                            width: 24,
                            height: 24,
                            border: `1px solid darkgrey`,
                          }}
                        />
                        <Typography variant="inherit">
                          {option.label}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Typography sx={{ mt: 1 }}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.line')}</Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <MuiColorInput
                  fullWidth
                  size="small"
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.color')}
                  isAlphaHidden={false}
                  onChange={(value: string, colors: MuiColorInputColors) => {
                    setSelectedShapes("stroke_style", colors.hex8);
                  }}
                  value={shape?.stroke_style ?? ""}
                />
                {/* line style */}
                <TextField
                  size="small"
                  fullWidth
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.lineStyle')}
                  select
                  value={shape?.line_style || "solid"}
                  onChange={(event) => {
                    setSelectedShapes("line_style", event.target.value);
                  }}
                >
                  {[...LINE_STYLES].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.lineWidth')}
                  inputProps={{ min: 1, max: 20, step: 1 }}
                  onChange={(event) => {
                    setSelectedShapes("line_width", Number(event.target.value));
                  }}
                  value={shape?.line_width ?? 1}
                />
              </Stack>
              <Typography sx={{ mt: 1 }}>{t('settings.rampsSettings.rampEditor.shapeEdit.styling.text')}</Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.fontSize')}
                  inputProps={{ min: 10, max: 64, step: 1 }}
                  onChange={(event) => {
                    setSelectedShapes("font_size", Number(event.target.value));
                  }}
                  value={shape?.font_size ?? 12}
                />
              </Stack>

              {/* Text Label Controls - Line shapes with text only */}
              {shape?.type === "Line" && shape?.text && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={
                          (shape.metadata as LineMetadata)?.show_text !== false
                        }
                        onChange={(e) => {
                          const updatedShapes = shapes.map((s) => {
                            if (
                              activeShapes.includes(s.id) ||
                              s.id === contextMenu?.shapeId
                            ) {
                              return {
                                ...s,
                                metadata: {
                                  ...s.metadata,
                                  show_text: e.target.checked,
                                },
                              };
                            }
                            return s;
                          });
                          setShapes(updatedShapes);
                        }}
                      />
                    }
                    label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.showText')}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    select
                    label={t('settings.rampsSettings.rampEditor.shapeEdit.styling.textPosition')}
                    value={
                      (shape.metadata as LineMetadata)?.text_position ||
                      "middle"
                    }
                    onChange={(e) => {
                      const updatedShapes = shapes.map((s) => {
                        if (
                          activeShapes.includes(s.id) ||
                          s.id === contextMenu?.shapeId
                        ) {
                          return {
                            ...s,
                            metadata: {
                              ...s.metadata,
                              text_position: e.target.value,
                            },
                          };
                        }
                        return s;
                      });
                      setShapes(updatedShapes);
                    }}
                    SelectProps={{
                      MenuProps: { PaperProps: { sx: { zIndex: 9999 } } },
                    }}
                  >
                    {TEXT_POSITION_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              )}
            </Stack>
          </TabPanel>

          {/* Tab 2 (conditional): Algorithm */}
          {showAlgorithmTab && (
            <TabPanel value={activeTab} index={2}>
              <Stack direction="column" spacing={2}>
                {/* Algorithm-specific fields */}
                {(shape?.tags?.includes("parkable") ||
                  shape?.tags?.includes("reference_line")) && (
                  <>
                    <Typography sx={{ mt: 2 }}>{t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.settings')}</Typography>

                    {/* Priority field for both parkable areas and reference lines */}
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.priority')}
                      </Typography>
                      <Slider
                        value={shape?.priority ?? 5}
                        onChange={(_, value) =>
                          setSelectedShapes("priority", value)
                        }
                        min={0}
                        max={10}
                        step={0.1}
                        marks={[
                          { value: 0, label: "0" },
                          { value: 5, label: "5" },
                          { value: 10, label: "10" },
                        ]}
                        valueLabelDisplay="auto"
                      />
                    </Box>

                    {/* Orientation field only for reference lines */}
                    {shape?.tags?.includes("reference_line") && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.aircraftOrientation')}
                        </Typography>
                        <Stack direction="column" spacing={1}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(shape?.orientation || []).includes(
                                  "nose_to_line"
                                )}
                                onChange={(e) => {
                                  const currentOrientations =
                                    shape?.orientation || [];
                                  if (e.target.checked) {
                                    if (
                                      !currentOrientations.includes(
                                        "nose_to_line"
                                      )
                                    ) {
                                      setSelectedShapes("orientation", [
                                        ...currentOrientations,
                                        "nose_to_line",
                                      ]);
                                    }
                                  } else {
                                    setSelectedShapes(
                                      "orientation",
                                      currentOrientations.filter(
                                        (o) => o !== "nose_to_line"
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noseToLine')}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={(shape?.orientation || []).includes(
                                  "tail_to_line"
                                )}
                                onChange={(e) => {
                                  const currentOrientations =
                                    shape?.orientation || [];
                                  if (e.target.checked) {
                                    if (
                                      !currentOrientations.includes(
                                        "tail_to_line"
                                      )
                                    ) {
                                      setSelectedShapes("orientation", [
                                        ...currentOrientations,
                                        "tail_to_line",
                                      ]);
                                    }
                                  } else {
                                    setSelectedShapes(
                                      "orientation",
                                      currentOrientations.filter(
                                        (o) => o !== "tail_to_line"
                                      )
                                    );
                                  }
                                }}
                              />
                            }
                            label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.tailToLine')}
                          />
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                {/* Spot reference points */}
                {shape?.tags?.includes("spot") && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.referencePoints')}
                    </Typography>
                    <Table size="small" sx={{ mt: 1, tableLayout: "fixed", width: "100%" }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ py: 0.5, px: 1, width: 80 }}></TableCell>
                          <TableCell
                            align="center"
                            sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}
                          >
                            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.nose')}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}
                          >
                            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.tail')}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}
                          >
                            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.overhang')}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ py: 0.5, px: 0.5, fontSize: "0.75rem", width: 60 }}
                          >
                            {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.orientations.center')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[
                          {
                            name: "north" as const,
                            label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.north.label'),
                            tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.north.tooltip'),
                          },
                          {
                            name: "south" as const,
                            label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.south.label'),
                            tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.south.tooltip'),
                          },
                          {
                            name: "east" as const,
                            label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.east.label'),
                            tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.east.tooltip'),
                          },
                          {
                            name: "west" as const,
                            label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.west.label'),
                            tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.west.tooltip'),
                          },
                          {
                            name: "center" as const,
                            label: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.center.label'),
                            tooltip: t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.refPoints.center.tooltip'),
                          },
                        ].map((refPoint) => {
                          const handleOrientationChange = (
                            orientation: OrientationType,
                            checked: boolean
                          ) => {
                            const currentRefPoints = [
                              ...(shape?.reference_points || []),
                            ];
                            const index = currentRefPoints.findIndex(
                              (rp) => rp.name === refPoint.name
                            );

                            if (checked) {
                              if (index >= 0) {
                                const currentOrientations =
                                  currentRefPoints[index].validOrientations;
                                if (
                                  !currentOrientations.includes(orientation)
                                ) {
                                  currentRefPoints[index].validOrientations = [
                                    ...currentOrientations,
                                    orientation,
                                  ];
                                }
                              } else {
                                currentRefPoints.push({
                                  name: refPoint.name,
                                  validOrientations: [orientation],
                                });
                              }
                            } else {
                              if (index >= 0) {
                                const newOrientations = currentRefPoints[
                                  index
                                ].validOrientations.filter(
                                  (o) => o !== orientation
                                );
                                if (newOrientations.length === 0) {
                                  currentRefPoints.splice(index, 1);
                                } else {
                                  currentRefPoints[index].validOrientations =
                                    newOrientations;
                                }
                              }
                            }
                            setSelectedShapes(
                              "reference_points",
                              currentRefPoints
                            );
                          };

                          const refPointData = (
                            shape?.reference_points || []
                          ).find((rp) => rp.name === refPoint.name);
                          const isNoseChecked =
                            refPointData?.validOrientations.includes(
                              "nose_to_line"
                            ) || false;
                          const isTailChecked =
                            refPointData?.validOrientations.includes(
                              "tail_to_line"
                            ) || false;
                          const isCenterChecked =
                            refPointData?.validOrientations.includes(
                              "center"
                            ) || false;
                          const isOverhangChecked =
                            refPointData?.validOrientations.includes(
                              "overhang"
                            ) || false;

                          return (
                            <TableRow key={refPoint.name}>
                              <TableCell sx={{ py: 0.5, px: 1, width: 80 }}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.5}
                                >
                                  <Typography variant="body2">
                                    {refPoint.label}
                                  </Typography>
                                  <Tooltip
                                    title={refPoint.tooltip}
                                    placement="right"
                                  >
                                    <IconButton size="small" sx={{ p: 0 }}>
                                      <InfoOutlinedIcon
                                        sx={{
                                          fontSize: 14,
                                          color: "text.secondary",
                                        }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ py: 0.5, px: 0.5, width: 60 }}
                              >
                                {refPoint.name !== "center" ? (
                                  <Checkbox
                                    size="small"
                                    checked={isNoseChecked}
                                    onChange={(e) =>
                                      handleOrientationChange(
                                        "nose_to_line",
                                        e.target.checked
                                      )
                                    }
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ py: 0.5, px: 0.5, width: 60 }}
                              >
                                {refPoint.name !== "center" ? (
                                  <Checkbox
                                    size="small"
                                    checked={isTailChecked}
                                    onChange={(e) =>
                                      handleOrientationChange(
                                        "tail_to_line",
                                        e.target.checked
                                      )
                                    }
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ py: 0.5, px: 0.5, width: 60 }}
                              >
                                {refPoint.name !== "center" ? (
                                  <Checkbox
                                    size="small"
                                    checked={isOverhangChecked}
                                    onChange={(e) =>
                                      handleOrientationChange(
                                        "overhang",
                                        e.target.checked
                                      )
                                    }
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell
                                align="center"
                                sx={{ py: 0.5, px: 0.5, width: 60 }}
                              >
                                {refPoint.name === "center" ? (
                                  <Checkbox
                                    size="small"
                                    checked={isCenterChecked}
                                    onChange={(e) =>
                                      handleOrientationChange(
                                        "center",
                                        e.target.checked
                                      )
                                    }
                                    sx={{ p: 0 }}
                                  />
                                ) : (
                                  <Typography
                                    variant="body2"
                                    color="text.disabled"
                                  >
                                    {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.noValue')}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* Spot properties for temporal parking algorithm */}
                {shape?.type === "Spot" && (
                  <SpotPropertiesSection
                    shape={shape}
                    shapes={shapes}
                    activeShapes={activeShapes}
                    contextMenu={contextMenu}
                    setShapes={setShapes}
                    canvasWidth={canvasWidth}
                    previewSpots={previewSpots}
                    setPreviewSpots={setPreviewSpots}
                  />
                )}

                {/* Zone properties for temporal parking algorithm */}
                {shape?.type === "Zone" && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneProperties')}
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                      {/* Disabled Toggle */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              (shape.metadata as ZoneMetadata)?.disabled ||
                              false
                            }
                            onChange={(e) => {
                              const updatedShapes = shapes.map((s) => {
                                if (
                                  activeShapes.includes(s.id) ||
                                  s.id === contextMenu?.shapeId
                                ) {
                                  return {
                                    ...s,
                                    metadata: {
                                      ...s.metadata,
                                      disabled: e.target.checked,
                                    },
                                  };
                                }
                                return s;
                              });
                              setShapes(updatedShapes);
                            }}
                          />
                        }
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography variant="body2">
                              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneDisabled.label')}
                            </Typography>
                            <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneDisabled.tooltip')}>
                              <IconButton size="small" sx={{ p: 0 }}>
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                      />

                      {/* Staging Area Toggle */}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={
                              (shape.metadata as ZoneMetadata)?.is_staging ||
                              false
                            }
                            onChange={(e) => {
                              const updatedShapes = shapes.map((s) => {
                                if (
                                  activeShapes.includes(s.id) ||
                                  s.id === contextMenu?.shapeId
                                ) {
                                  return {
                                    ...s,
                                    metadata: {
                                      ...s.metadata,
                                      is_staging: e.target.checked,
                                      // Reset overflow parking when disabling staging
                                      allow_overflow_parking: e.target.checked
                                        ? (s.metadata as any)
                                            ?.allow_overflow_parking
                                        : false,
                                    },
                                  };
                                }
                                return s;
                              });
                              setShapes(updatedShapes);
                            }}
                          />
                        }
                        label={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.5}
                          >
                            <Typography variant="body2">
                              {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.stagingArea.label')}
                            </Typography>
                            <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.stagingArea.tooltip')}>
                              <IconButton size="small" sx={{ p: 0 }}>
                                <InfoOutlinedIcon
                                  sx={{ fontSize: 14, color: "text.secondary" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        }
                      />

                      {/* Allow Overflow Parking Toggle (only visible for staging zones) */}
                      {(shape.metadata as ZoneMetadata)?.is_staging && (
                        <FormControlLabel
                          sx={{ ml: 2 }}
                          control={
                            <Switch
                              checked={
                                (shape.metadata as any)
                                  ?.allow_overflow_parking || false
                              }
                              onChange={(e) => {
                                const updatedShapes = shapes.map((s) => {
                                  if (
                                    activeShapes.includes(s.id) ||
                                    s.id === contextMenu?.shapeId
                                  ) {
                                    return {
                                      ...s,
                                      metadata: {
                                        ...s.metadata,
                                        allow_overflow_parking: e.target.checked,
                                      },
                                    };
                                  }
                                  return s;
                                });
                                setShapes(updatedShapes);
                              }}
                            />
                          }
                          label={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                            >
                              <Typography variant="body2">
                                {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.overflowParking.label')}
                              </Typography>
                              <Tooltip title={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.overflowParking.tooltip')}>
                                <IconButton size="small" sx={{ p: 0 }}>
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: "text.secondary",
                                    }}
                                  />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          }
                        />
                      )}

                      <TextField
                        size="small"
                        fullWidth
                        label={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.label')}
                        value={shape?.text || ""}
                        onChange={(e) =>
                          setSelectedShapes("text", e.target.value)
                        }
                        placeholder={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.placeholder')}
                        helperText={t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zoneName.helperText')}
                      />
                      <Box>
                        <Typography variant="body2" gutterBottom>
                          {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zonePriority')}
                        </Typography>
                        <Slider
                          value={shape?.priority ?? 5}
                          onChange={(_, value) =>
                            setSelectedShapes("priority", value)
                          }
                          min={0}
                          max={10}
                          step={1}
                          marks={[
                            { value: 0, label: "0" },
                            { value: 5, label: "5" },
                            { value: 10, label: "10" },
                          ]}
                          valueLabelDisplay="auto"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {t('settings.rampsSettings.rampEditor.shapeEdit.algorithm.zonePriorityHelp')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                )}
              </Stack>
            </TabPanel>
          )}
        </Box>

        {/* Actions - Always visible at bottom (sticky) */}
        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" justifyContent="space-between" width="100%">
            <Button
              color="error"
              onClick={() => {
                // delete this shape and all selected shapes
                const updatedShapes = shapes.filter(
                  (s) =>
                    !activeShapes.includes(s.id) && s.id !== contextMenu.shapeId
                );
                setActiveShapes([]);
                setShapes(updatedShapes);
                setContextMenu(null);
              }}
            >
              {t('settings.rampsSettings.rampEditor.shapeEdit.actions.delete')}
            </Button>
            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => {
                  // Ensure we have a valid ramp context
                  if (!ramp || !ramp.id || !ramp.fbo_id) {
                    console.error(
                      "Cannot copy shape - ramp not properly loaded"
                    );
                    return;
                  }

                  const updatedShapes = [...shapes];
                  updatedShapes.push({
                    ...shape,
                    id: uuidv4(), // Generate new ID for the copied shape
                    ramp_id: ramp.id, // Explicitly use current ramp's ID
                    fbo_id: ramp.fbo_id, // Explicitly use current FBO's ID
                  });
                  setShapes(updatedShapes);
                }}
              >
                {t('settings.rampsSettings.rampEditor.shapeEdit.actions.copy')}
              </Button>
              <Button onClick={onClose}>{t('settings.rampsSettings.rampEditor.shapeEdit.actions.cancel')}</Button>
              <Button
                variant="contained"
                onClick={() => {
                  // Re-detect required spots if this is a gap spot
                  if (
                    shape?.type === "Spot" &&
                    (shape.metadata as SpotMetadata)?.isGapSpot
                  ) {
                    const requiredSpotIds = detectRequiredSpots(shape, shapes);
                    const updatedShapes = shapes.map((s) => {
                      if (s.id === shape.id) {
                        return {
                          ...s,
                          metadata: {
                            ...s.metadata,
                            requiresOccupiedSpots: requiredSpotIds,
                          },
                        };
                      }
                      return s;
                    });
                    setShapes(updatedShapes);
                  }
                  onClose();
                }}
              >
                {t('settings.rampsSettings.rampEditor.shapeEdit.actions.ok')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};
